// Research > Tools (T-381, owner request 2026-11-05): screening tools run by HOST jobs; this page only reads their stored results.
// Tool 1 "Midterms": earnings-driven capitulation into long-term support, over every symbol we hold (2,869 on 2026-11-05; was the 210-stock watchlist).
// Data: GET /api/tools/midterms = the row scripts/ops/midterms/midterms_screen.py writes into tool_results (bars: Alpaca SIP daily, split-adjusted,
// scripts/ops/midterms/pull_daily_300w.py; stage B P/S + earnings beat: Finnhub free tier, survivors of the chart rules only). Read-only, no orders.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { tools as api } from '../lib/api'
import { Panel, MONO } from '../components/deskParts'

const th = { textAlign: 'left', color: '#64748b', fontWeight: 400, padding: '2px 10px 2px 0', cursor: 'pointer', whiteSpace: 'nowrap' }
const td = { padding: '2px 10px 2px 0', color: '#cbd5e1', whiteSpace: 'nowrap' }
const OK = '#34d399', NO = '#475569', WARN = '#fbbf24'
const ORDER = ['r1', 'r2', 'r3', 'r4a', 'r4b', 'r4c', 'r4d']
const SHORT = { r1: 'P/S<10', r2: '200W ±2%', r3: '5 down wks', r4a: '200D rising', r4b: 'gap ≤-7%', r4c: 'tests 200D', r4d: 'EPS+rev beat' }

// one rule cell: pass/fail mark plus the measured value the script stored (never computed here)
function RuleCell({ r, k }) {
  if (!r) return <td style={{ ...td, color: NO }} title="not checked: only stocks passing every chart rule are checked against P/S and earnings">·</td>
  const v = k === 'r2' ? (r.dist_pct != null ? `${r.dist_pct > 0 ? '+' : ''}${r.dist_pct}%` : 'short hist')
    : k === 'r3' ? r.down_weeks
    : k === 'r4b' ? (r.gap_pct != null ? `${r.gap_pct}%` : '')
    : k === 'r1' ? (r.ps_ttm ?? r.reason)
    : k === 'r4d' ? (r.report_date ? `${r.report_date} ${r.eps_beat ? 'EPS✓' : 'EPS✗'} ${r.rev_beat ? 'rev✓' : 'rev✗'}` : r.reason)
    : (r.reason ? 'short hist' : '')
  return <td style={{ ...td, color: r.pass ? OK : NO }}>{r.pass ? '✓' : '✗'} {v}</td>
}

function Midterms() {
  const [d, setD] = useState(null)
  const [err, setErr] = useState(null)
  const [sort, setSort] = useState('local_passed')
  const [onlyNear, setOnlyNear] = useState(true)
  const load = useCallback(() => { api.result('midterms').then(r => { setD(r); setErr(null) }).catch(e => setErr(String(e.message || e))) }, [])
  useEffect(() => { load(); const t = setInterval(load, 300000) /* T-381: about 2,870 rows, changes twice a day: every 5 min */; return () => clearInterval(t) }, [load])
  const rows = useMemo(() => {
    if (!d || !d.rows) return []
    const r = onlyNear ? d.rows.filter(x => x.local_passed >= 3) : d.rows
    const key = (x) => sort === 'symbol' ? x.symbol : sort === 'local_passed' ? -x.local_passed : (x.rules[sort]?.pass ? 0 : 1)
    return [...r].sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : a.symbol.localeCompare(b.symbol)))
  }, [d, sort, onlyNear])
  if (err) return <p style={{ color: '#fca5a5' }}>{err}</p>
  if (!d) return <p style={{ color: '#64748b' }}>loading…</p>
  if (d.available === false) return <p style={{ color: WARN }}>No result yet: {d.reason}. Run: .venv/bin/python scripts/ops/midterms/pull_daily_300w.py && .venv/bin/python scripts/ops/midterms/midterms_screen.py</p>
  const stale = d.age_s > 36 * 3600
  return (<>
    <Panel title="Midterms: earnings capitulation into the 200-week SMA"
      right={<span style={{ color: stale ? '#f87171' : '#64748b' }}>session {d.session}{d.partial ? ' (in progress)' : ''} · run {Math.round(d.age_s / 60)} min ago{stale ? ' · STALE' : ''}</span>}>
      <p style={{ ...MONO, fontSize: '0.72rem', marginBottom: 6 }}>{d.digest} <span style={{ color: '#64748b' }}>[{d.digest_source === 'model' ? 'local model draft, checked by the number audit' : 'script'}]</span></p>
      <div className="flex flex-wrap gap-4" style={{ ...MONO, fontSize: '0.72rem' }}>
        <span>Screened: {d.screened ?? d.universe}{d.screened != null && d.screened !== d.universe ? ` of ${d.universe}` : ''}</span>
        {['r2', 'r3', 'r4a', 'r4b', 'r4c'].map(k => <span key={k}>{SHORT[k]}: {d.per_rule_pass[k]}</span>)}
        <span>All chart rules: <b style={{ color: d.stage_a_survivors.length ? OK : NO }}>{d.stage_a_survivors.length}</b></span>
        <span>P/S + earnings checked: {d.stage_b_checked}</span>
        <span>HITS: <b style={{ color: d.hits.length ? OK : NO }}>{d.hits.length ? d.hits.join(', ') : 'none'}</b></span>
      </div>
      {/* T-381 items 3-5: what was NOT screened or could not be checked is shown, never hidden behind "no hits" */}
      {d.stage_b_errors?.length > 0 && <p style={{ ...MONO, fontSize: '0.7rem', color: '#f87171', marginTop: 6 }}>
        Finnhub data missing for {d.stage_b_errors.join(', ')}: their P/S or earnings rule could not be checked (the run is marked FAILED).</p>}
      {d.stale?.length > 0 && <p style={{ ...MONO, fontSize: '0.7rem', color: WARN, marginTop: 6 }}>
        Stale, not screened ({d.stale.length}): {d.stale.map(x => `${x.symbol} (last bar ${x.last_bar})`).join(', ')}.</p>}
      {d.missing?.length > 0 && <p style={{ ...MONO, fontSize: '0.7rem', color: WARN, marginTop: 6 }}>
        In the universe but no stored bars ({d.missing.length}): {d.missing.join(', ')}.</p>}
      <p style={{ ...MONO, fontSize: '0.64rem', color: '#64748b', marginTop: 6 }}>
        Order: chart rules on stored data first (all {d.universe}), then P/S and the earnings beat from Finnhub only for stocks that pass every chart rule.
        {d.insufficient_history.length > 0 && ` ${d.insufficient_history.length} stocks have under 200 weeks of history and cannot pass: ${d.insufficient_history.join(', ')}.`}
        {' '}Screen output, not a backtested signal.
      </p>
    </Panel>
    <Panel title="Per stock" right={<label style={{ fontSize: '0.68rem' }}><input type="checkbox" checked={onlyNear} onChange={e => setOnlyNear(e.target.checked)} /> only stocks passing 3+ chart rules</label>}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...MONO, fontSize: '0.68rem', width: '100%' }}>
          <thead><tr>
            <th style={th} onClick={() => setSort('symbol')}>Symbol</th>
            <th style={th} onClick={() => setSort('local_passed')}>Chart rules</th>
            <th style={th}>Close</th>
            {ORDER.map(k => <th key={k} style={th} title={d.rule_labels[k]} onClick={() => setSort(k)}>{SHORT[k]}</th>)}
          </tr></thead>
          <tbody>{rows.map(x => (
            <tr key={x.symbol} style={{ background: d.hits.includes(x.symbol) ? 'rgba(52,211,153,0.08)' : undefined }}>
              <td style={{ ...td, color: x.stale ? WARN : '#e2e8f0' }} title={x.stale ? `stale: last bar ${x.session}, not screened` : undefined}>{x.symbol}{x.stale ? ` (stale ${x.session})` : ''}</td><td style={td}>{x.local_passed}/5</td><td style={td}>{x.close?.toFixed(2)}</td>
              {ORDER.map(k => <RuleCell key={k} k={k} r={x.rules[k]} />)}
            </tr>))}
          </tbody>
        </table>
        {rows.length === 0 && <p style={{ color: '#64748b', fontSize: '0.7rem' }}>No stock passes 3 or more chart rules. Untick the filter to see all {d.universe}.</p>}
      </div>
    </Panel>
  </>)
}

export default function Tools() {
  return (
    <div className="p-3 space-y-3" style={{ color: '#cbd5e1' }}>
      <div style={{ ...MONO, fontSize: '0.8rem', color: '#e2e8f0' }}>TOOLS <span style={{ color: '#64748b', fontSize: '0.65rem' }}>research screens run by host jobs; this page reads their last result. Read-only, paper only, no orders.</span></div>
      <Midterms />
    </div>
  )
}
